/**
 * Queries multiple ArcGIS feature service layers for attributes at a specific geographic point.
 *
 * This function sends parallel `query` requests to each specified layer ID within a feature service,
 * using the provided longitude and latitude as the query geometry. It returns a mapping of layer IDs
 * to arrays of feature attribute objects found at that location.
 */

import esriRequest from "@arcgis/core/request";
import { HttpProxyConfig } from "../types/config";
import { proxify } from "./proxy";

type LonLat = { longitude: number; latitude: number };

export async function queryLayersAtPoint(
  featureServiceBaseUrl: string,
  layerIds: number[],
  center: LonLat,
  proxy?: HttpProxyConfig,
  geometry?: any
): Promise<Record<number, any[]>> {
  const base = featureServiceBaseUrl.replace(/\/+$/, "");

  // Use full parcel geometry if provided, otherwise fall back to center point
  let geom: string;
  let geometryType: string;
  let inSR: string;

  if (geometry) {
    // Detect geometry type based on structure
    const hasRings = geometry.rings && Array.isArray(geometry.rings);
    const hasPaths = geometry.paths && Array.isArray(geometry.paths);
    const hasXY = typeof geometry.x === 'number' && typeof geometry.y === 'number';

    if (hasRings) {
      // It's a polygon
      geom = JSON.stringify(geometry);
      geometryType = "esriGeometryPolygon";
    } else if (hasPaths) {
      // It's a polyline
      geom = JSON.stringify(geometry);
      geometryType = "esriGeometryPolyline";
    } else if (hasXY) {
      // It's just a point, use it as-is
      geom = JSON.stringify(geometry);
      geometryType = "esriGeometryPoint";
    } else {
      // Unknown geometry, fall back to center point
      console.warn('Unknown geometry type, falling back to center point');
      geom = JSON.stringify({
        x: center.longitude,
        y: center.latitude,
        spatialReference: { wkid: 4326 }
      });
      geometryType = "esriGeometryPoint";
    }

    // Extract the spatial reference from the geometry
    const sr = geometry.spatialReference;
    inSR = sr?.wkid?.toString() || sr?.latestWkid?.toString() || "4326";
  } else {
    geom = JSON.stringify({
      x: center.longitude,
      y: center.latitude,
      spatialReference: { wkid: 4326 }
    });
    geometryType = "esriGeometryPoint";
    inSR = "4326";
  }

  const TIMEOUT_MS = 10_000;

  const pointGeom = JSON.stringify({
    x: center.longitude,
    y: center.latitude,
    spatialReference: { wkid: 4326 }
  });

  async function queryLayer(id: number, useGeom: string, useGeometryType: string, useInSR: string, usePost: boolean): Promise<any[]> {
    const url = proxify(`${base}/${id}/query`, proxy);
    const params = new URLSearchParams({
      f: "json",
      where: "1=1",
      geometry: useGeom,
      geometryType: useGeometryType,
      inSR: useInSR,
      spatialRel: "esriSpatialRelIntersects",
      outFields: "*",
      outSR: "4326",
      returnGeometry: "false"
    });

    const requestOptions = usePost
      ? { method: "post" as const, body: params.toString(), headers: { "Content-Type": "application/x-www-form-urlencoded" }, responseType: "json" as const }
      : { query: Object.fromEntries(params), responseType: "json" as const };

    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), TIMEOUT_MS)
    );

    const response = await Promise.race([esriRequest(url, requestOptions), timeout]);
    const json = (response as any).data;
    return (json.features ?? []).map((f: any) => f.attributes || {});
  }

  const byLayer: Record<number, any[]> = {};
  const usePost = !!geometry && geometryType !== "esriGeometryPoint";

  await Promise.all(
    layerIds.map(async (id) => {
      try {
        byLayer[id] = await queryLayer(id, geom, geometryType, inSR, usePost);
      } catch {
        // Retry with center point on timeout or error
        try {
          byLayer[id] = await queryLayer(id, pointGeom, "esriGeometryPoint", "4326", false);
        } catch {
          byLayer[id] = [];
        }
      }
    })
  );
  return byLayer;
}